import { useAuth } from "@clerk/nextjs";
import path from "constants/path";
import { useRouter } from "next/router";

/**
 * A hook that ensures the user is signed in before continuing.
 */
export function useRequireSignIn() {
  const router = useRouter();
  const { isSignedIn, isLoaded } = useAuth();

  /**
   * Redirects to the sign in page if the user is not signed in.
   * @param redirectPath - The path to redirect to after signing in.
   * @returns true if the user is signed in, false otherwise.
   */
  const redirectIfNotSignedIn = (redirectPath?: string): boolean => {
    return redirectToAuthIfNotSignedIn(path.SIGN_IN, redirectPath);
  };

  /**
   * Redirects to the sign up page if the user is not signed in.
   * @param redirectPath - The path to redirect to after signing up.
   * @returns true if the user is signed in, false otherwise.
   */
  const redirectToSignUpIfNotSignedIn = (redirectPath?: string): boolean => {
    return redirectToAuthIfNotSignedIn(path.SIGN_UP, redirectPath);
  };

  /**
   * Redirects to the specified auth page if the user is not signed in.
   * @param authPath - The authentication path to redirect to (SIGN_IN or SIGN_UP).
   * @param redirectPath - The path to redirect to after authentication.
   * @returns true if the user is signed in, false otherwise.
   */
  const redirectToAuthIfNotSignedIn = (
    authPath: string,
    redirectPath?: string
  ): boolean => {
    if (!isLoaded) return false;

    if (!isSignedIn) {
      const finalPath = redirectPath ?? router.asPath;
      const redirect = `/?redirect_url=${encodeURIComponent(finalPath)}`;
      router.push(`${authPath}${redirect}`);
      return false;
    }

    return true;
  };

  return { redirectIfNotSignedIn, redirectToSignUpIfNotSignedIn };
}
